import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  cropRect,
  defaultFrame,
  type FaceBox,
  type Frame,
  type ImageSize,
  isSmallPhoto,
  MAX_ZOOM,
  panBy,
  pinchStep,
  zoomAt,
} from '../utils/photoFrame';

/**
 * Frame a profile photo before it is saved (Nicky, 13 Oct 2026): uploads used
 * to be center-cropped into the circle by CSS, which cut off the top of heads.
 *
 * The photo shows in a square with the circle it displays in marked; drag to
 * move, pinch / wheel / trackpad / slider to zoom, keyboard arrows to nudge and
 * + / - to zoom. Save exports the square (at most 800 px, JPEG) and hands it to
 * the caller, which uploads it exactly as before.
 *
 * Self-contained styling (plain Tailwind, not the dashboard tokens) because it
 * opens from both the dashboard and the /start signup page. It is an in-page
 * dialog, so nothing native covers the iOS webview.
 */

type Drawable = { source: CanvasImageSource; size: ImageSize };

/** Free a decoded bitmap's pixels now rather than whenever the browser gets to it. */
function release(d: Drawable | null) {
  if (d && typeof ImageBitmap !== 'undefined' && d.source instanceof ImageBitmap) d.source.close();
}

/** Decoded images are capped so a 48MP phone photo can't exhaust iOS canvas memory. */
const DECODE_MAX = 4096;

async function decode(blob: Blob): Promise<Drawable> {
  let source: CanvasImageSource | null = null;
  let size: ImageSize | null = null;
  // EXIF rotation: createImageBitmap honors it with imageOrientation
  // 'from-image'; where that option isn't supported, an <img> applies it.
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
      source = bitmap;
      size = { w: bitmap.width, h: bitmap.height };
    } catch {
      source = null;
    }
  }
  if (!source) {
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      await img.decode();
      source = img;
      size = { w: img.naturalWidth, h: img.naturalHeight };
    } finally {
      // The decoded pixels stay usable for drawing after the URL is revoked.
      URL.revokeObjectURL(url);
    }
  }
  if (!source || !size || size.w < 1 || size.h < 1) throw new Error('decode failed');
  const longest = Math.max(size.w, size.h);
  if (longest <= DECODE_MAX) return { source, size };
  const scale = DECODE_MAX / longest;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(size.w * scale);
  canvas.height = Math.round(size.h * scale);
  canvas.getContext('2d')?.drawImage(source, 0, 0, canvas.width, canvas.height);
  if (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap) source.close();
  return { source: canvas, size: { w: canvas.width, h: canvas.height } };
}

/** A face, if this browser ships a cheap detector (the Shape Detection API). Never required. */
async function detectFace(source: CanvasImageSource): Promise<FaceBox | null> {
  const Detector = (window as any).FaceDetector;
  if (typeof Detector !== 'function') return null;
  try {
    const detector = new Detector({ fastMode: true, maxDetectedFaces: 1 });
    const faces = await Promise.race([
      detector.detect(source),
      new Promise<null>((resolve) => window.setTimeout(() => resolve(null), 700)),
    ]);
    const box = faces?.[0]?.boundingBox;
    return box && box.width > 0 ? { x: box.x, y: box.y, width: box.width, height: box.height } : null;
  } catch {
    return null;
  }
}

function exportSquare(d: Drawable, frame: Frame): Promise<File> {
  const { sx, sy, side, out } = cropRect(d.size, frame);
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('canvas unavailable'));
  // JPEG has no transparency: a transparent PNG would otherwise turn black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out, out);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(d.source, sx, sy, side, side, 0, 0, out, out);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' })) : reject(new Error('export failed'))),
      'image/jpeg',
      0.9
    );
  });
}

const KEY_NUDGE = 8;
const ZOOM_STEP = 1.1;

export type PhotoFramerProps = {
  /** The photo to frame: a picked file, or a stored photo fetched as a blob. */
  source: Blob;
  /** Upload the framed square. Throwing keeps the editor open with the message. */
  onSave: (file: File) => Promise<void>;
  onCancel: () => void;
  /** Reframing a stored photo (not a new pick): enables the small-photo note. */
  existing?: boolean;
  /** id of the page's photo file input, for the "upload the original" link. */
  reuploadInputId?: string;
};

export default function PhotoFramer({ source, onSave, onCancel, existing, reuploadInputId }: PhotoFramerProps) {
  const [drawable, setDrawable] = useState<Drawable | null>(null);
  const [frame, setFrame] = useState<Frame | null>(null);
  const [initial, setInitial] = useState<Frame | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(320);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; mx: number; my: number } | null>(null);
  // Captured during the first render, before any effect moves focus, so close
  // returns it to whatever opened the editor.
  const returnFocus = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null
  );

  // Decode, then pick the default framing (face hint if available).
  // Each decoded bitmap is released when the photo changes or the editor
  // closes, including a decode that finishes after that.
  useEffect(() => {
    let cancelled = false;
    let current: Drawable | null = null;
    // A replacement photo (e.g. "upload the original") removes the control
    // that had focus: keep focus in the dialog while the new one loads.
    if (!panelRef.current?.contains(document.activeElement)) panelRef.current?.focus();
    setDrawable(null);
    setFrame(null);
    setLoadError(null);
    setSaveError(null);
    (async () => {
      try {
        const d = await decode(source);
        if (cancelled) {
          release(d);
          return;
        }
        current = d;
        const face = await detectFace(d.source);
        if (cancelled) return;
        const f = defaultFrame(d.size, face);
        setDrawable(d);
        setFrame(f);
        setInitial(f);
      } catch {
        if (!cancelled) setLoadError('We couldn’t open that photo. Try a JPG or PNG.');
      }
    })();
    return () => {
      cancelled = true;
      release(current);
    };
  }, [source]);

  // The square fits the screen: 360 px, less on a narrow phone or a short
  // (landscape) one, leaving room for the controls; the panel also scrolls.
  useLayoutEffect(() => {
    const fit = () => setView(Math.max(180, Math.min(360, window.innerWidth - 72, window.innerHeight - 300)));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  // Focus moves into the dialog and comes back on close.
  useEffect(() => {
    const opener = returnFocus.current;
    // Into the dialog right away, so Tab and Escape work while the photo
    // loads and after a load error; the framing area takes it once ready.
    panelRef.current?.focus();
    return () => opener?.focus?.();
  }, []);
  useEffect(() => {
    if (drawable) stageRef.current?.focus();
  }, [drawable]);

  // Draw the framed square.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !drawable || !frame) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const px = Math.round(view * dpr);
    if (canvas.width !== px) canvas.width = px;
    if (canvas.height !== px) canvas.height = px;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { sx, sy, side } = cropRect(drawable.size, frame);
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, px, px);
    ctx.drawImage(drawable.source, sx, sy, side, side, 0, 0, px, px);
  }, [drawable, frame, view]);

  const update = useCallback(
    (fn: (f: Frame, size: ImageSize) => Frame) => {
      if (!drawable) return;
      setFrame((f) => (f ? fn(f, drawable.size) : f));
    },
    [drawable]
  );

  const zoomBy = (factor: number, fx = view / 2, fy = view / 2) =>
    update((f, size) => zoomAt(size, f, f.zoom * factor, fx, fy, view));

  // Wheel / trackpad pinch: a non-passive listener so the page doesn't scroll.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
      update((f, size) => zoomAt(size, f, f.zoom * factor, e.clientX - rect.left, e.clientY - rect.top, view));
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [update, view, drawable]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drawable) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    pinch.current = null;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const next = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, next);
    const all = Array.from(pointers.current.values());
    if (all.length === 1) {
      update((f, size) => panBy(size, f, next.x - prev.x, next.y - prev.y, view));
      return;
    }
    if (all.length >= 2) {
      const [a, b] = all;
      const rect = e.currentTarget.getBoundingClientRect();
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mx = (a.x + b.x) / 2 - rect.left;
      const my = (a.y + b.y) / 2 - rect.top;
      const last = pinch.current;
      pinch.current = { dist, mx, my };
      if (!last || last.dist < 1) return;
      update((f, size) => pinchStep(size, f, dist / last.dist, { x: last.mx, y: last.my }, { x: mx, y: my }, view));
    }
  };

  const onPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    pinch.current = null;
  };

  const onStageKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? KEY_NUDGE * 4 : KEY_NUDGE;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    if (moves[e.key]) {
      e.preventDefault();
      const [dx, dy] = moves[e.key];
      update((f, size) => panBy(size, f, dx, dy, view));
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      zoomBy(ZOOM_STEP);
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      zoomBy(1 / ZOOM_STEP);
    } else if (e.key === '0' && initial) {
      e.preventDefault();
      setFrame(initial);
    }
  };

  // Escape cancels; Tab stays inside the dialog.
  const onPanelKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && !saving) {
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), [tabindex="0"]')
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const at = focusable.indexOf(document.activeElement as HTMLElement);
    // Wrap at either end, and from the panel itself (it holds focus while a
    // photo loads, or after the file picker is canceled).
    if (at === -1) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && at === 0) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && at === focusable.length - 1) {
      e.preventDefault();
      first.focus();
    }
  };

  const save = async () => {
    if (!drawable || !frame || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const file = await exportSquare(drawable, frame);
      await onSave(file);
    } catch (err: any) {
      setSaveError(err?.message && err.message !== 'export failed' ? err.message : 'We couldn’t save that photo. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const small = existing && drawable && isSmallPhoto(drawable.size);
  const zoomPct = frame ? Math.round(frame.zoom * 100) : 100;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="photo-framer-title"
        aria-describedby="photo-framer-help"
        tabIndex={-1}
        onKeyDown={onPanelKey}
        className="max-h-full w-full max-w-[420px] space-y-4 overflow-y-auto overscroll-contain rounded-2xl bg-neutral-900 p-5 text-white shadow-2xl outline-none ring-1 ring-white/10"
      >
        <div>
          <h2 id="photo-framer-title" className="text-lg font-semibold">
            Frame your photo
          </h2>
          <p id="photo-framer-help" className="mt-1 text-sm text-neutral-300">
            Drag to move it. Pinch, scroll or use the slider to zoom. On a keyboard, arrows move it and + and − zoom.
          </p>
        </div>

        <div
          ref={stageRef}
          tabIndex={0}
          role="group"
          aria-roledescription="photo framing area"
          aria-label={`Photo framing, ${zoomPct}% zoom. Arrow keys move the photo, plus and minus zoom.`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onKeyDown={onStageKey}
          className="relative mx-auto cursor-grab touch-none select-none overflow-hidden rounded-xl bg-neutral-800 outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900 active:cursor-grabbing"
          style={{ width: view, height: view }}
        >
          <canvas ref={canvasRef} className="block h-full w-full" aria-hidden="true" />
          {/* The circle it shows in; the corners outside it are dimmed. */}
          <div
            className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-white/80"
            style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)' }}
            aria-hidden="true"
          />
          {!drawable && !loadError && (
            <div className="absolute inset-0 grid place-items-center text-sm text-neutral-300" role="status">
              Opening photo…
            </div>
          )}
          {loadError && (
            <div className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-red-300" role="alert">
              {loadError}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => zoomBy(1 / ZOOM_STEP)}
            disabled={!drawable || (frame?.zoom ?? 1) <= 1}
            aria-label="Zoom out"
            className="grid size-10 shrink-0 place-items-center rounded-lg bg-white/10 text-xl leading-none hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:opacity-40"
          >
            −
          </button>
          <input
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={frame?.zoom ?? 1}
            disabled={!drawable}
            onChange={(e) => {
              const z = Number(e.target.value);
              update((f, size) => zoomAt(size, f, z, view / 2, view / 2, view));
            }}
            aria-label="Zoom"
            aria-valuetext={`${zoomPct}%`}
            className="h-2 min-w-0 flex-1 cursor-pointer accent-sky-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          />
          <button
            type="button"
            onClick={() => zoomBy(ZOOM_STEP)}
            disabled={!drawable || (frame?.zoom ?? 1) >= MAX_ZOOM}
            aria-label="Zoom in"
            className="grid size-10 shrink-0 place-items-center rounded-lg bg-white/10 text-xl leading-none hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:opacity-40"
          >
            +
          </button>
        </div>

        {small && drawable && (
          <p className="rounded-lg bg-amber-400/10 px-3 py-2 text-sm text-amber-200" role="note">
            This photo is only {drawable.size.w}×{drawable.size.h}, so it’s probably already cropped.
            For a sharper result,{' '}
            {reuploadInputId ? (
              // A button that clicks the page's file input: reachable by
              // keyboard, and the iOS picker shim handles input.click().
              <button
                type="button"
                onClick={() => {
                  // This button goes away once a new photo is picked; park
                  // focus on the dialog first so it never leaves it.
                  panelRef.current?.focus();
                  (document.getElementById(reuploadInputId) as HTMLInputElement | null)?.click();
                }}
                className="font-semibold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
              >
                upload the original
              </button>
            ) : (
              'upload the original'
            )}
            .
          </p>
        )}

        {saveError && (
          <p className="text-sm text-red-300" role="alert">
            {saveError}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="h-11 flex-1 rounded-xl bg-white/10 text-sm font-semibold hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={!drawable || saving}
            className="h-11 flex-1 rounded-xl bg-sky-500 text-sm font-semibold text-white hover:bg-sky-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Fetch a stored photo for re-framing. Storage sends CORS, and its hosts are in the CSP's connect-src. */
export async function fetchStoredPhoto(url: string): Promise<Blob> {
  const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
  if (!res.ok) throw new Error(`photo fetch ${res.status}`);
  const blob = await res.blob();
  if (!blob.type.startsWith('image/')) throw new Error('not an image');
  return blob;
}
